import type { RefObject } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { GenerateImageProps } from '@atlassian/ai-image-generation/types';

import Button from '@atlaskit/button/button';
import ImageIcon from '@atlaskit/icon/core/image';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';

import type { UnsplashResult } from '@atlassian/company-hub-common/media-components/unsplash/types';

import { UnsplashPopupMenuContent } from './UnsplashPopupMenuContent.tsx';

export type UnsplashPopupMenuProps = {
	imageSrc?: string;
	imageAltText?: string;
	initialUnsplashInfoArray: UnsplashResult[] | undefined;
	closePopup: () => void;
	openMediaBrowser?: () => void;
	coverPictureState: any;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	handleUnsplashUpload: (url: string) => void;
	floatingToolbarButtonStyle?: object;
	onAddHeaderImageButtonClicked?: () => void;
	onAddHeaderImageButtonHovered?: () => void;
	userOffline?: boolean;
	isModalOpenState: boolean;
	onDelete?: () => void;
	onReposition?: () => void;
	dropdownRef?: RefObject<HTMLElement | null>;
	dropdownPopupComponentRef?: RefObject<HTMLElement | null>;
	contentId: string;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
};

const i18n = defineMessages({
	headerLabel: {
		id: 'content-topper.add.header.image.message.floating-toolbar',
		description:
			'Message displayed on the button which allows the user to change the header image in a page in the floating toolbar.',
		defaultMessage: 'Header image',
	},
	updateImage: {
		id: 'content-topper.page-title.popup.update.header.image',
		description: 'Message user can click on to update/change their header image.',
		defaultMessage: 'Update header image',
	},
});

export const UnsplashPopupMenu = ({
	isModalOpenState,
	closePopup,
	initialUnsplashInfoArray,
	openMediaBrowser,
	userOffline,
	handleUnsplashUpload,
	setIsUploadingPicture,
	coverPictureState,
	floatingToolbarButtonStyle,
	onAddHeaderImageButtonClicked,
	onAddHeaderImageButtonHovered,
	onReposition,
	onDelete,
	imageSrc,
	dropdownRef,
	dropdownPopupComponentRef,
	contentId,
	generateAIHeaderImage,
}: UnsplashPopupMenuProps): React.JSX.Element => {
	return (
		<Popup
			isOpen={isModalOpenState}
			onClose={closePopup}
			placement="bottom-start"
			role="dialog"
			// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
			shouldRenderToParent={false}
			content={() => (
				<UnsplashPopupMenuContent
					dropdownRef={dropdownRef}
					imageSrc={imageSrc}
					initialUnsplashInfoArray={initialUnsplashInfoArray}
					openMediaBrowser={openMediaBrowser}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					coverPictureState={coverPictureState}
					closePopup={closePopup}
					onReposition={onReposition}
					onDelete={onDelete}
					dropdownPopupComponentRef={dropdownPopupComponentRef}
					contentId={contentId}
					isPageTitleComponent
					userOffline={userOffline}
					generateAIHeaderImage={generateAIHeaderImage}
				/>
			)}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					isSelected={isModalOpenState}
					appearance="subtle"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={floatingToolbarButtonStyle || {}}
					testId="add-header-image-button"
					spacing="compact"
					iconBefore={<ImageIcon label="" />}
					onClick={onAddHeaderImageButtonClicked}
					isDisabled={userOffline}
					onMouseEnter={onAddHeaderImageButtonHovered}
				>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<span className="add-header-image-button">
						<FormattedMessage {...i18n.headerLabel} />
					</span>
				</Button>
			)}
		/>
	);
};
